import { useCallback, useSyncExternalStore } from 'react';

import { DEV_CONSOLE_VISIT_HISTORY_MAX_ENTRIES, DEV_CONSOLE_VISIT_HISTORY_STORAGE_KEY } from '@/config/devConsole';

export interface VisitEntry {
  path: string;
  label: string;
  type: 'page' | 'subpage' | 'patient' | 'provider' | 'job' | 'workflow' | 'queue';
  visitedAt: number;
  visitCount: number;
}

function readEntries(): VisitEntry[] {
  try {
    const raw = localStorage.getItem(DEV_CONSOLE_VISIT_HISTORY_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as VisitEntry[]) : [];
  } catch {
    return [];
  }
}

// Tiny external-store so every hook instance re-renders on change
const listeners = new Set<() => void>();
let snapshot = readEntries();

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot() {
  return snapshot;
}

function writeEntries(entries: VisitEntry[]) {
  const trimmed = entries.slice(0, DEV_CONSOLE_VISIT_HISTORY_MAX_ENTRIES);
  localStorage.setItem(DEV_CONSOLE_VISIT_HISTORY_STORAGE_KEY, JSON.stringify(trimmed));
  snapshot = trimmed;
  listeners.forEach((fn) => fn());
}

export function recordVisit(path: string, label: string, type: VisitEntry['type']) {
  const entries = readEntries();
  const idx = entries.findIndex((e) => e.path === path);

  if (idx >= 0) {
    entries[idx].label = label;
    entries[idx].visitedAt = Date.now();
    entries[idx].visitCount += 1;
  } else {
    entries.push({ path, label, type, visitedAt: Date.now(), visitCount: 1 });
  }

  writeEntries(entries);
}

export function useVisitHistory() {
  const entries = useSyncExternalStore(subscribe, getSnapshot);

  const recentVisits = useCallback(
    (n: number) => [...entries].sort((a, b) => b.visitedAt - a.visitedAt).slice(0, n),
    [entries]
  );

  const mostVisited = useCallback(
    (n: number) => [...entries].sort((a, b) => b.visitCount - a.visitCount || b.visitedAt - a.visitedAt).slice(0, n),
    [entries]
  );

  return { recentVisits, mostVisited, recordVisit };
}
